// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  writeBatch, 
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  count,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';

// Analytics and Activity Tracking entity interfaces
export interface CreateAnalyticsEventData {
  eventType: 'courseCreated' | 'courseUpdated' | 'courseDeleted' |
              'lessonCompleted' | 'quizAttempted' | 'quizCompleted' |
              'enrollmentCreated' | 'enrollmentCompleted' | 'certificateIssued' |
              'resourceViewed' | 'resourceDownloaded' | 'connectCreated' |
              'connectCompleted' | 'paymentProcessed' | 'subscriptionChanged' |
              'userRegistered' | 'userLogin' | 'reviewCreated';
  eventData: Record<string, any>;
  userId?: string;
  accountId?: string; // For multi-tenant support
  session_id?: string;
  ipAddress?: string;
  userAgent?: string;
  referrer?: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

export interface CreateActivityLogData {
  activityType: 'create' | 'update' | 'delete' | 'view' | 'download' | 'enroll' | 'complete' | 'payment' | 'login' | 'logout';
  entityType: 'user' | 'course' | 'module' | 'lesson' | 'quiz' | 'resource' | 'connect' | 'enrollment' | 'certificate' | 'review' | 'subscription';
  entityId: string;
  userId: string;
  accountId?: string;
  actionDetails: Record<string, any>;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  session_id?: string;
  timestamp: Date;
}

export interface CreateMetricData {
  metricType: 'userCount' | 'courseCount' | 'enrollmentCount' | 'revenue' | 'pageViews' | 'sessionDuration' | 'completionRate';
  metricValue: number;
  metricUnit?: string;
  dimensions: Record<string, string>; // e.g., { period: 'daily', accountId: '123' }
  accountId?: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

export interface AnalyticsFilter {
  eventType?: string;
  userId?: string;
  accountId?: string;
  entityType?: string;
  entityId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  activityType?: string;
}

export interface GetAnalyticsEventData {
  id: string;
  eventType: string;
  eventData: Record<string, any>;
  userId?: string;
  accountId?: string;
  session_id?: string;
  ipAddress?: string;
  userAgent?: string;
  referrer?: string;
  timestamp: any;
  metadata?: Record<string, any>;
}

export interface GetActivityLogData {
  id: string;
  activityType: string;
  entityType: string;
  entityId: string;
  userId: string;
  accountId?: string;
  actionDetails: Record<string, any>;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  session_id?: string;
  timestamp: any;
}

export interface GetMetricData {
  id: string;
  metricType: string;
  metricValue: number;
  metricUnit?: string;
  dimensions: Record<string, string>;
  accountId?: string;
  timestamp: any;
  metadata?: Record<string, any>;
}

// Account context for all operations
export interface AccountContext {
  accountId?: string;
  userId?: string;
  session_id?: string;
  ipAddress?: string;
  userAgent?: string;
  referrer?: string;
}

export class AnalyticsRepository {
  constructor() {
    console.log('[AnalyticsRepository] Constructor - DB instance available:', !!db);
  }

  private eventsCollectionName = 'analyticsEvents';
  private activitiesCollectionName = 'activityLogs';
  private metricsCollectionName = 'analyticsMetrics';

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>,
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  // ===== ANALYTICS EVENTS =====

  async createAnalyticsEvent(
    eventData: CreateAnalyticsEventData,
    context?: AccountContext
  ): Promise<GetAnalyticsEventData> {
    try {
      const enrichedEventData = {
        ...eventData,
        accountId: eventData.accountId || context?.accountId,
        session_id: eventData.session_id || context?.session_id,
        ipAddress: eventData.ipAddress || context?.ipAddress,
        userAgent: eventData.userAgent || context?.userAgent,
        referrer: eventData.referrer || context?.referrer,
        userId: eventData.userId || context?.userId,
        timestamp: eventData.timestamp || serverTimestamp(),
      };

      // Every enrichment field above is optional, and Firestore rejects an
      // explicit `undefined` outright. Writing the object as-is threw
      // "Unsupported field value: undefined (found in field accountId)" and,
      // because callers await this, took the caller's own write down with it —
      // course creation failed purely because its telemetry could not be
      // recorded. Drop absent keys instead of writing them as undefined.
      const cleanedEventData = Object.fromEntries(
        Object.entries(enrichedEventData).filter(([, v]) => v !== undefined)
      );

      const docRef = await addDoc(collection(db, this.eventsCollectionName), cleanedEventData);

      return {
        id: docRef.id,
        ...cleanedEventData,
      } as GetAnalyticsEventData;
    } catch (error) {
      console.error('Error creating analytics event:', error);
      throw this.toRepositoryError(error, 'ANALYTICS_EVENT_CREATE_FAILED', 'Failed to create analytics event', {
        eventType: eventData.eventType,
        userId: eventData.userId || context?.userId,
        accountId: eventData.accountId || context?.accountId,
        sessionId: eventData.session_id || context?.session_id,
      });
    }
  }

  async getAnalyticsEvents(
    filter?: AnalyticsFilter,
    limitCount?: number,
    context?: AccountContext
  ): Promise<GetAnalyticsEventData[]> {
    try {
      let q = collection(db, this.eventsCollectionName);

      const conditions: any[] = [];

      if (filter?.accountId || context?.accountId) {
        conditions.push(where('accountId', '==', filter?.accountId || context?.accountId));
      }

      if (filter?.userId || context?.userId) {
        conditions.push(where('userId', '==', filter?.userId || context?.userId));
      }

      if (filter?.eventType) {
        conditions.push(where('eventType', '==', filter?.eventType));
      }

      if (filter?.dateFrom) {
        conditions.push(where('timestamp', '>=', filter.dateFrom));
      }

      if (filter?.dateTo) {
        conditions.push(where('timestamp', '<=', filter.dateTo));
      }

      if (conditions.length > 0) {
        q = query(q, ...conditions, orderBy('timestamp', 'desc'));
      } else {
        q = query(q, orderBy('timestamp', 'desc'));
      }

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const events: GetAnalyticsEventData[] = [];

      querySnapshot.forEach((doc) => {
        events.push({
          id: doc.id,
          ...doc.data(),
        } as GetAnalyticsEventData);
      });

      return events;
    } catch (error) {
      console.error('Error getting analytics events:', error);
      throw this.toRepositoryError(error, 'ANALYTICS_EVENT_LIST_FAILED', 'Failed to get analytics events', {
        filter,
        limitCount,
        accountId: context?.accountId,
        userId: context?.userId,
      });
    }
  }

  // ===== ACTIVITY LOGS =====

  async createActivityLog(
    activityData: CreateActivityLogData,
    context?: AccountContext
  ): Promise<GetActivityLogData> {
    try {
      const enrichedActivityData = {
        ...activityData,
        accountId: activityData.accountId || context?.accountId,
        session_id: activityData.session_id || context?.session_id,
        ipAddress: activityData.ipAddress || context?.ipAddress,
        userAgent: activityData.userAgent || context?.userAgent,
        timestamp: activityData.timestamp || serverTimestamp(),
      };

      // accountId / session_id / ipAddress / userAgent are often absent, and
      // Firestore rejects an undefined field -- so every activity log written
      // without an account context (creating a course, for one) was lost.
      const docRef = await addDoc(
        collection(db, this.activitiesCollectionName),
        Object.fromEntries(Object.entries(enrichedActivityData).filter(([, v]) => v !== undefined)),
      );

      return {
        id: docRef.id,
        ...enrichedActivityData,
      } as GetActivityLogData;
    } catch (error) {
      console.error('Error creating activity log:', error);
      throw this.toRepositoryError(error, 'ACTIVITY_LOG_CREATE_FAILED', 'Failed to create activity log', {
        activityType: activityData.activityType,
        entityType: activityData.entityType,
        entityId: activityData.entityId,
        userId: activityData.userId,
        accountId: activityData.accountId || context?.accountId,
      });
    }
  }

  async getActivityLogs(
    filter?: AnalyticsFilter,
    limitCount?: number,
    context?: AccountContext
  ): Promise<GetActivityLogData[]> {
    try {
      let q = collection(db, this.activitiesCollectionName);

      const conditions: any[] = [];

      if (filter?.accountId || context?.accountId) {
        conditions.push(where('accountId', '==', filter?.accountId || context?.accountId));
      }

      if (filter?.userId || context?.userId) {
        conditions.push(where('userId', '==', filter?.userId || context?.userId));
      }

      if (filter?.entityType) {
        conditions.push(where('entityType', '==', filter?.entityType));
      }

      if (filter?.entityId) {
        conditions.push(where('entityId', '==', filter?.entityId));
      }

      if (filter?.activityType) {
        conditions.push(where('activityType', '==', filter?.activityType));
      }

      if (filter?.dateFrom) {
        conditions.push(where('timestamp', '>=', filter.dateFrom));
      }

      if (filter?.dateTo) {
        conditions.push(where('timestamp', '<=', filter.dateTo));
      }

      if (conditions.length > 0) {
        q = query(q, ...conditions, orderBy('timestamp', 'desc'));
      } else {
        q = query(q, orderBy('timestamp', 'desc'));
      }

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const activities: GetActivityLogData[] = [];

      querySnapshot.forEach((doc) => {
        activities.push({
          id: doc.id,
          ...doc.data(),
        } as GetActivityLogData);
      });

      return activities;
    } catch (error) {
      console.error('Error getting activity logs:', error);
      throw this.toRepositoryError(error, 'ACTIVITY_LOG_LIST_FAILED', 'Failed to get activity logs', {
        filter,
        limitCount,
        accountId: context?.accountId,
        userId: context?.userId,
      });
    }
  }

  // ===== METRICS =====

  async createMetric(
    metricData: CreateMetricData,
    context?: AccountContext
  ): Promise<GetMetricData> {
    try {
      const enrichedMetricData = {
        ...metricData,
        accountId: metricData.accountId || context?.accountId,
        timestamp: metricData.timestamp || serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, this.metricsCollectionName), enrichedMetricData);

      return {
        id: docRef.id,
        ...enrichedMetricData,
      } as GetMetricData;
    } catch (error) {
      console.error('Error creating metric:', error);
      throw this.toRepositoryError(error, 'ANALYTICS_METRIC_CREATE_FAILED', 'Failed to create metric', {
        metricType: metricData.metricType,
        metricValue: metricData.metricValue,
        accountId: metricData.accountId || context?.accountId,
        dimensions: metricData.dimensions,
      });
    }
  }

  async getMetrics(
    filter?: {
      metricType?: string;
      accountId?: string;
      dateFrom?: Date;
      dateTo?: Date;
      dimensions?: Record<string, string>;
    },
    limitCount?: number,
    context?: AccountContext
  ): Promise<GetMetricData[]> {
    try {
      let q = collection(db, this.metricsCollectionName);

      const conditions: any[] = [];

      if (filter?.accountId || context?.accountId) {
        conditions.push(where('accountId', '==', filter?.accountId || context?.accountId));
      }

      if (filter?.metricType) {
        conditions.push(where('metricType', '==', filter?.metricType));
      }

      if (filter?.dateFrom) {
        conditions.push(where('timestamp', '>=', filter.dateFrom));
      }

      if (filter?.dateTo) {
        conditions.push(where('timestamp', '<=', filter.dateTo));
      }

      if (conditions.length > 0) {
        q = query(q, ...conditions, orderBy('timestamp', 'desc'));
      } else {
        q = query(q, orderBy('timestamp', 'desc'));
      }

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const metrics: GetMetricData[] = [];

      querySnapshot.forEach((doc) => {
        metrics.push({
          id: doc.id,
          ...doc.data(),
        } as GetMetricData);
      });

      return metrics;
    } catch (error) {
      console.error('Error getting metrics:', error);
      throw this.toRepositoryError(error, 'ANALYTICS_METRIC_LIST_FAILED', 'Failed to get metrics', {
        filter,
        limitCount,
        accountId: context?.accountId,
      });
    }
  }

  // ===== AGGREGATED ANALYTICS =====

  async getUserActivitySummary(
    userId: string,
    dateRange?: { start: Date; end: Date },
    context?: AccountContext
  ): Promise<{
    totalEvents: number;
    eventBreakdown: Record<string, number>;
    activityBreakdown: Record<string, number>;
    lastActivity: Date | null;
    mostActiveDay: string | null;
  }> {
    try {
      const filter: AnalyticsFilter = {
        userId: userId,
        dateFrom: dateRange?.start,
        dateTo: dateRange?.end,
      };

      const events = await this.getAnalyticsEvents(filter, 1000, context);
      const activities = await this.getActivityLogs(filter, 1000, context);

      const eventBreakdown: Record<string, number> = {};
      const activityBreakdown: Record<string, number> = {};
      const dayActivity: Record<string, number> = {};

      events.forEach(event => {
        eventBreakdown[event.eventType] = (eventBreakdown[event.eventType] || 0) + 1;

        if (event.timestamp) {
          const day = event.timestamp.toDate().toISOString().split('T')[0];
          dayActivity[day] = (dayActivity[day] || 0) + 1;
        }
      });

      activities.forEach(activity => {
        activityBreakdown[activity.activityType] = (activityBreakdown[activity.activityType] || 0) + 1;
      });

      const lastActivity = events.length > 0
        ? events[0].timestamp?.toDate() || null
        : null;

      const mostActiveDay = Object.keys(dayActivity).length > 0
        ? Object.entries(dayActivity).reduce((a, b) => dayActivity[a[0]] > dayActivity[b[0]] ? a : b)[0]
        : null;

      return {
        totalEvents: events.length,
        eventBreakdown,
        activityBreakdown,
        lastActivity,
        mostActiveDay,
      };
    } catch (error) {
      console.error('Error getting user activity summary:', error);
      throw this.toRepositoryError(error, 'USER_ACTIVITY_SUMMARY_FAILED', 'Failed to get user activity summary', {
        userId,
        dateRange,
        accountId: context?.accountId,
      });
    }
  }

  async getPlatformAnalytics(
    dateRange?: { start: Date; end: Date },
    context?: AccountContext
  ): Promise<{
    totalUsers: number;
    totalCourses: number;
    totalEnrollments: number;
    totalRevenue: number;
    userGrowth: Array<{ date: string; count: number }>;
    revenueByMonth: Array<{ month: string; revenue: number }>;
    topCourses: Array<{ courseId: string; enrollments: number; revenue: number }>;
    conversionRates: {
      visitorToUser: number;
      userToEnrolled: number;
      enrolledToCompleted: number;
    };
  }> {
    try {
      // Get metrics data
      const metrics = await this.getMetrics({
        dateFrom: dateRange?.start,
        dateTo: dateRange?.end,
      }, undefined, context);

      // Aggregate metrics
      const aggregatedMetrics = {
        totalUsers: 0,
        totalCourses: 0,
        totalEnrollments: 0,
        totalRevenue: 0,
      };

      const userGrowth: Array<{ date: string; count: number }> = [];
      const revenueByMonth: Array<{ month: string; revenue: number }> = [];

      metrics.forEach(metric => {
        switch (metric.metricType) {
          case 'userCount':
            aggregatedMetrics.totalUsers = metric.metricValue;
            break;
          case 'courseCount':
            aggregatedMetrics.totalCourses = metric.metricValue;
            break;
          case 'enrollmentCount':
            aggregatedMetrics.totalEnrollments = metric.metricValue;
            break;
          case 'revenue':
            aggregatedMetrics.totalRevenue += metric.metricValue;
            break;
        }
      });

      // Mock additional data (would be calculated from events/activities)
      const topCourses = [
        { courseId: 'course1', enrollments: 150, revenue: 4500 },
        { courseId: 'course2', enrollments: 120, revenue: 3600 },
        { courseId: 'course3', enrollments: 95, revenue: 2850 },
      ];

      const conversionRates = {
        visitorToUser: 0.15, // 15% of visitors become users
        userToEnrolled: 0.25, // 25% of users enroll in courses
        enrolledToCompleted: 0.70, // 70% of enrolled students complete courses
      };

      return {
        ...aggregatedMetrics,
        userGrowth,
        revenueByMonth,
        topCourses,
        conversionRates,
      };
    } catch (error) {
      console.error('Error getting platform analytics:', error);
      throw this.toRepositoryError(error, 'PLATFORM_ANALYTICS_FAILED', 'Failed to get platform analytics', {
        dateRange,
        accountId: context?.accountId,
      });
    }
  }

  // ===== BULK OPERATIONS =====

  async bulkCreateActivityLogs(
    activityLogs: CreateActivityLogData[],
    context?: AccountContext
  ): Promise<GetActivityLogData[]> {
    try {
      const batch = writeBatch(db);
      const results: GetActivityLogData[] = [];

      activityLogs.forEach((activityData, index) => {
        const enrichedData = {
          ...activityData,
          accountId: activityData.accountId || context?.accountId,
          session_id: activityData.session_id || context?.session_id,
          ipAddress: activityData.ipAddress || context?.ipAddress,
          userAgent: activityData.userAgent || context?.userAgent,
          timestamp: activityData.timestamp || serverTimestamp(),
        };

        const docRef = doc(collection(db, this.activitiesCollectionName));
        batch.set(docRef, enrichedData);

        results.push({
          id: docRef.id,
          ...enrichedData,
        } as GetActivityLogData);
      });

      await batch.commit();
      return results;
    } catch (error) {
      console.error('Error bulk creating activity logs:', error);
      throw this.toRepositoryError(error, 'ACTIVITY_LOG_BULK_CREATE_FAILED', 'Failed to bulk create activity logs', {
        activityLogCount: activityLogs.length,
        accountId: context?.accountId,
        userId: context?.userId,
      });
    }
  }

  // ===== UTILITY METHODS =====

  async trackEntityActivity(
    entityType: CreateActivityLogData['entityType'],
    entityId: string,
    activityType: CreateActivityLogData['activityType'],
    userId: string,
    actionDetails: Record<string, any>,
    context?: AccountContext
  ): Promise<GetActivityLogData> {
    return this.createActivityLog({
      activityType,
      entityType,
      entityId: entityId,
      userId: userId,
      actionDetails: actionDetails,
      timestamp: new Date(),
    }, context);
  }

  // Get all analytics events (admin only)
  async getAllAnalytics(limitCount?: number): Promise<GetAnalyticsEventData[]> {
    try {
      let q = query(
        collection(db, this.eventsCollectionName),
        orderBy('timestamp', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      const analytics: GetAnalyticsEventData[] = [];

      querySnapshot.forEach((doc) => {
        analytics.push({
          id: doc.id,
          ...doc.data(),
        } as GetAnalyticsEventData);
      });

      return analytics;
    } catch (error) {
      console.error('Error getting all analytics:', error);
      throw this.toRepositoryError(error, 'ANALYTICS_EVENT_LIST_ALL_FAILED', 'Failed to get all analytics events', {
        limitCount,
      });
    }
  }

  async trackAnalyticsEvent(
    eventType: CreateAnalyticsEventData['eventType'],
    eventData: Record<string, any>,
    userId?: string,
    context?: AccountContext
  ): Promise<GetAnalyticsEventData> {
    return this.createAnalyticsEvent({
      eventType,
      eventData: eventData,
      userId: userId,
      timestamp: new Date(),
    }, context);
  }

  // ===== ADMIN ANALYTICS (absorbed from firestore.ts) =====

  async getAdminAnalytics(_timeRange: string = '30d'): Promise<any> {
    try {
      // Use server-side count aggregation instead of reading every document in
      // each collection. Previously this pulled the ENTIRE users/courses/
      // enrollments collections to the client just to call `.length` — cost and
      // latency grew with the whole database. getCountFromServer reads a single
      // aggregated result.
      const safeCount = async (q: Parameters<typeof getCountFromServer>[0]): Promise<number> => {
        try {
          return (await getCountFromServer(q)).data().count;
        } catch {
          return 0;
        }
      };

      const [totalUsers, totalCourses, totalEnrollments, professionals] = await Promise.all([
        safeCount(collection(db, 'users')),
        safeCount(collection(db, 'certificationCourses')),
        safeCount(collection(db, 'enrollments')),
        safeCount(
          query(
            collection(db, 'users'),
            where('roles', 'array-contains-any', ['INSTRUCTOR', 'PROFESSIONAL'])
          )
        ),
      ]);

      return {
        totalUsers,
        totalCourses,
        totalEnrollments,
        professionals,
        students: totalUsers - professionals,
        activeUsers: totalUsers > 0 ? Math.floor(totalUsers * 0.7) : 0,
        conversionRate: totalEnrollments > 0 && totalUsers > 0
          ? (totalEnrollments / totalUsers * 100).toFixed(1)
          : '0',
      };
    } catch (error) {
      throw new DRXError({
        code: 'ADMIN_ANALYTICS_FAILED',
        devMessage: 'Failed to fetch admin analytics',
        cause: error,
      });
    }
  }

  async getRevenueAnalytics(_timeRange: string = '30d'): Promise<any> {
    try {
      // Server-side aggregation instead of reading the entire orders /
      // subscriptions collections. `sum('amount')` ignores non-numeric values
      // (returns 0), matching the old `parseFloat(...) || 0` behaviour.
      let totalRevenue = 0;
      let orderCount = 0;
      let activeSubscriptions = 0;

      try {
        const ordersAgg = await getAggregateFromServer(collection(db, 'orders'), {
          total: sum('amount'),
          count: count(),
        });
        totalRevenue = ordersAgg.data().total ?? 0;
        orderCount = ordersAgg.data().count ?? 0;
      } catch {}

      try {
        activeSubscriptions = (
          await getCountFromServer(
            query(collection(db, 'subscriptions'), where('status', '==', 'active'))
          )
        ).data().count;
      } catch {}

      const subscriptionRevenue = activeSubscriptions * 29.99;

      return {
        totalRevenue: totalRevenue + subscriptionRevenue,
        subscriptionRevenue,
        courseRevenue: totalRevenue,
        growthRate: totalRevenue > 0 ? 15.2 : 0,
        monthlyRecurringRevenue: subscriptionRevenue,
        averageOrderValue: orderCount > 0 ? totalRevenue / orderCount : 0,
      };
    } catch (error) {
      throw new DRXError({
        code: 'REVENUE_ANALYTICS_FAILED',
        devMessage: 'Failed to fetch revenue analytics',
        cause: error,
      });
    }
  }
}
